import { PagedApiResult } from "../../models/utilities/paged-request";
import { BaseService } from "../../services/_base-service";
import { DealerCredit } from "../../support/models/dealers/dealer-credit";
import { DealerCreditSearchFilter } from "../../support/models/dealers/dealer-credit-search-filters";
import { DealerDetailVM, DealerRead } from "../../support/models/dealers/dealer-read";
import { DealerSales } from "../../support/models/dealers/dealer-sales";
import { UpdateCreditReversalStatusCommand } from "../../support/models/dealers/update-dealer-credit";
import { Api, ApiResponse, StatusCode } from "../../utils/api";
import { ApiEndpoints, Routes } from "../../utils/app-constants";
import { navigateToPage } from "../../utils/history";
import { ShipToAddressRead, ShipToAddressWithOrders } from "../models/ship-to-address-read";

export class DealerSvc extends BaseService {

    public getDealer = async (id : number) : Promise<ApiResponse<DealerRead>> => {
        return await this._get(
            DealerRead, 
            id,
            ApiEndpoints.DEALERS_BY_ID
        );
    }

    /**
     * Get the subtotals of the invoices of a dealer, by month, for the current year and the ones before it
     * @param id The dealer ID
     */
    public getSales = async (id : string) : Promise<ApiResponse<DealerSales>> => {
        return await this._get(
            DealerSales,
            id,
            ApiEndpoints.DEALERS_SALES
        );
    }

    public getFullDealer = async (id : string, silent? : boolean) : Promise<ApiResponse<DealerDetailVM>> => {

        const response = new ApiResponse<DealerDetailVM>();

        if (!id || id.length <= 0) {
            navigateToPage(Routes.SUPPORT_DEALERS);
        }
        else {

            this._showLoading();

            const HandledGet = async (object : any, url : string) : Promise<any> => {
                try {
                    const result = await Api.Get(object, url) as any;
                    if (result && result.id) {
                        return result;
                    }
                }
                catch (err) { /* Do Nothing */ }
                return undefined;
            };

            const HandledGetList = async (object : any, url : string) : Promise<any> => {
                try {
                    return await Api.GetList(object, url);
                }
                catch (err) { /* Do Nothing */ }
                return undefined;
            };

            const [
                dealer,
                addresses
            ] = await Promise.all([
                await HandledGet(DealerRead, ApiEndpoints.DEALERS_BY_ID.formattify(id)),
                await HandledGetList(ShipToAddressRead, ApiEndpoints.DEALERS_TRANSPORTATION_ADDRESSES.formattify(id))
            ]);
          
            const typedDealer = dealer as DealerRead;
            const typedAddress = addresses as ShipToAddressRead[];

            // Remove this if-block if we want to allow the user to load the
            // details and selectively hide sections of the page that aren't 
            // available
            const activeSC = this.store.getState().userdata.activeServiceCenter.name;
            if ((activeSC !== typedDealer.serviceCenterId) && (activeSC !== 'ALL')) {
                if (!silent) {
                    this._showToast("This dealer is associated with another service center.");
                }
                response.status = 202;
            }

            const geoLocatePayload = {
                addressLine1    : typedDealer.billingAddress.addressLine1,
                addressLine2    : typedDealer.billingAddress.addressLine2,
                city            : typedDealer.billingAddress.city,
                stateProvince   : typedDealer.billingAddress.stateProvince,
                postalCode      : typedDealer.billingAddress.postalCode
            };

            try {
                const geocodeResult = await Api.Post(ApiEndpoints.TRANSPORTATION_ADDRESS_LOCATE, geoLocatePayload);
                if (geocodeResult) {
                    typedDealer.billingAddress.latitude = geocodeResult.latitude;
                    typedDealer.billingAddress.longitude = geocodeResult.longitude;
                }
            }
            catch (err) { 
                typedDealer.billingAddress.latitude = 0.0;
                typedDealer.billingAddress.longitude = 0.0;
            }
                
            if (typedDealer.mailDocuments && typedDealer.mailDocuments.length > 0) {
                typedDealer.invoiceDocumentMailed = true;
            }
                        
            response.data = { 
                dealer: typedDealer, 
                addresses: typedAddress 
            };
            
            response.status = StatusCode.Success;        
        }

        // Should never hit this.
        this._hideLoading();
        return response;
    }

    public getDealerAddresses = async (dealerId : string) : Promise<ApiResponse<ShipToAddressRead[]>> => {

        this._showLoading();
        const response = new ApiResponse<ShipToAddressRead[]>();

        try {

            const aDetails = await Api.GetList(
                ShipToAddressRead, 
                ApiEndpoints.DEALERS_TRANSPORTATION_ADDRESSES.formattify(dealerId)
            );

            if (aDetails) {
                response.data = aDetails;
                response.status = StatusCode.Success;
            }
            
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getDealerAddressesWithOrders = async (dealerId : string, serviceCenterId : string) : Promise<ApiResponse<ShipToAddressWithOrders[]>> => {

        this._showLoading();
        const response = new ApiResponse<ShipToAddressWithOrders[]>();

        try {

            const aDetails = await Api.GetList(
                ShipToAddressWithOrders, 
                ApiEndpoints.DEALERS_TRANSPORTATION_OPEN_ADDRESSES.formattify(dealerId, serviceCenterId)
            );

            if (aDetails) {
                response.data = aDetails;
                response.status = StatusCode.Success;
            }
            
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getDealerCredits = async (searchFilters : DealerCreditSearchFilter) : Promise<ApiResponse<PagedApiResult<DealerCredit>>> => {
        return await this.search(DealerCredit, searchFilters);
    }

    public toggleDealerCredit = async (request : UpdateCreditReversalStatusCommand) : Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();
        
        const result = await this._put(
            ApiEndpoints.DEALERS_CREDIT_REVERSE.formattify(request.creditId), 
            request
        );

        if (result) {
            response.status = StatusCode.Success;
            response.data = true;
        }

        return response;

    }

    public saveDealer = async (dealer: DealerRead): Promise<ApiResponse<number | string>> => {

        let response = new ApiResponse<number | string>();
        this._showLoading();

        response = await this._updateDealer(dealer);

        this._hideLoading();
        return response;
    }

    private _updateDealer = async (dealer: DealerRead) => {
        let payload = this._getPayload(dealer);

        return await this._put<string>(
            ApiEndpoints.DEALERS_BY_ID.formattify(dealer.id),
            payload
        );
    }

    private _getPayload = (dealer: DealerRead) => {
        return {
            creditHoldReason: dealer.creditHoldReason,
            defaultShippingMethod: dealer.defaultShippingMethod,
            defaultShipToAddressId: dealer.defaultShipToAddressId
        };
    }
}
